/**
 * Shared data-prep for the three scoring-chain visualizations.
 *
 * `ScoringChainPanel` swaps between a list (A), a graph (B), and a
 * timeline (C). Keeping the partitioning + "skipped" derivation here
 * means all three views agree on what they're rendering.
 */
import {useMemo} from 'react';
import {
  AgentScore,
  AgentType,
  EvalAgentEntry,
  FeedbackLog,
  ScoreDirection,
  ScoreKind,
  SpecialistSelectionReason,
} from '../../../types/generated/graphql';
import {COLORS, getScoreColor} from '../../../utils/scoring';
import {pcolors} from '../../../theme/planapusTheme';

/**
 * A pairing of the agent score and any user/admin feedback for the
 * same specialization. `agent` and `feedback` are both optional so we
 * can represent:
 *   - agent only (normal scored specialist)
 *   - feedback only (user/admin rated a specialization that never had an agent score)
 *   - both (feedback override — UI shows feedback as the "active" rating with the agent row visible underneath)
 */
export interface SpecialistEntry {
  specialization: string;
  agent?: AgentScore;
  feedback?: FeedbackLog;
}

export interface ChainData {
  triage: AgentScore | undefined;
  final: AgentScore | undefined;
  // Paired entries — one per specialization present in either the
  // agent-score or feedback set for this video.
  specialists: SpecialistEntry[];
  // Unpaired agent scores only (legacy consumers). Excludes feedback.
  specialistScores: AgentScore[];
  // Unpaired feedback overrides (a feedback row with no matching agent row).
  feedbackOnly: FeedbackLog[];
  skipped: EvalAgentEntry[];
  didGate: boolean;
  shortCircuited: boolean;
  triageTags: Set<string>;
  triageFlags: Set<string>;
  // Populated when the channel gate short-circuited scoring for this
  // video. Carries the "skipped by channel gate" score row so the UX
  // can surface the channel evidence + reason.
  channelGateScore: AgentScore | undefined;
}

export function useChainData(
  scores: AgentScore[],
  agents: EvalAgentEntry[],
  childAge: number,
  feedback: FeedbackLog[] = [],
): ChainData {
  return useMemo(() => {
    // Channel-gate rows are always stage 2 + reason=CHANNEL_GATE. When
    // present they trump everything else — the pipeline never hit the
    // triage LLM, so there is no triage/specialists/final to show.
    const channelGateScore = scores.find(
      s => s.gatedContext?.selectionReason === SpecialistSelectionReason.ChannelGate,
    );
    const triage = scores.find(
      s =>
        s.gatedContext?.stage === 0 ||
        s.gatedContext?.selectionReason === SpecialistSelectionReason.Triage,
    );
    // A "final" score row is stage 2 but NOT a channel-gate short-circuit —
    // the channel-gate row is stored as a stage-2 row for UI convenience,
    // but it represents a skip, not a meta-agent verdict.
    const final = scores.find(
      s =>
        s.gatedContext?.stage === 2 &&
        s.gatedContext?.selectionReason !== SpecialistSelectionReason.ChannelGate,
    );
    // Legacy rows (no gatedContext) are treated as stage-1 specialists so
    // older videos still render somewhere in the chain views.
    const specialistScores = scores.filter(
      s => s.gatedContext == null || s.gatedContext.stage === 1,
    );

    // Pair each specialization with its latest agent score + latest
    // feedback row. Feedback rows that carry a matching specialization
    // override the agent's verdict in the aggregate — we need to show
    // both so admins can see what the agent said and what humans said.
    const pairMap = new Map<string, SpecialistEntry>();
    for (const s of specialistScores) {
      const spec = String(s.agent.specialization);
      pairMap.set(spec, {specialization: spec, agent: s});
    }
    // Keep only the most-recent feedback row per specialization — the
    // backend returns every historical rating and we only care about
    // the active one.
    const feedbackBySpec = new Map<string, FeedbackLog>();
    for (const fb of feedback) {
      if (fb.kind !== ScoreKind.UserFeedback) continue;
      const spec = String(fb.agent.specialization);
      const prev = feedbackBySpec.get(spec);
      if (!prev || Date.parse(fb.date) > Date.parse(prev.date)) {
        feedbackBySpec.set(spec, fb);
      }
    }
    const feedbackOnly: FeedbackLog[] = [];
    for (const [spec, fb] of feedbackBySpec) {
      const existing = pairMap.get(spec);
      if (existing) {
        existing.feedback = fb;
      } else {
        pairMap.set(spec, {specialization: spec, feedback: fb});
        feedbackOnly.push(fb);
      }
    }
    const specialists = Array.from(pairMap.values());
    const didGate = triage != null || channelGateScore != null;
    const shortCircuited =
      !!triage?.triageOutput?.shortCircuited || !!channelGateScore;
    const triageTags = new Set<string>(triage?.triageOutput?.categoryTags ?? []);
    const triageFlags = new Set<string>(triage?.triageOutput?.riskFlags ?? []);

    const ranBySpec = new Set(specialistScores.map(s => String(s.agent.specialization)));
    const skipped = didGate
      ? agents.filter(a => {
          if (a.type === AgentType.Meta) return false;
          if (childAge < a.min_age || childAge > a.max_age) return false;
          if (ranBySpec.has(String(a.specialization))) return false;
          if (String(a.specialization).endsWith('APPROPRIATENESS_TRIAGE')) return false;
          if (String(a.specialization).endsWith('APPROPRIATENESS')) return false;
          return true;
        })
      : [];

    return {
      triage,
      final,
      specialists,
      specialistScores,
      feedbackOnly,
      skipped,
      didGate,
      shortCircuited,
      triageTags,
      triageFlags,
      channelGateScore,
    };
  }, [scores, agents, childAge, feedback]);
}

// Shared label helpers — lifted out of the existing ScoringChainPanel so
// every view renders tags/flags identically.
export function humanizeTag(tag: string): string {
  return tag.replace(/_/g, ' ').toLowerCase();
}

export function humanizeFlag(flag: string): string {
  return flag.replace(/^POSSIBLE_/, '').replace(/_/g, ' ').toLowerCase();
}

/**
 * Triage / rating-string tone. USE THIS ONLY when you have a raw rating
 * label (HARMFUL / NEUTRAL / BENEFICIAL / INCONCLUSIVE) and no score or
 * direction — e.g. the triage verdict chip. For coloring anything driven
 * by a numeric `score` on an `AgentScore`, use `agentScoreTone` so the
 * color respects `scoreDirection`.
 */
export function verdictTone(v: string | null | undefined): {
  bg: string;
  fg: string;
  label: string;
} {
  const palette = {
    HARMFUL: {bg: '#FEE2E2', fg: '#991B1B', label: 'harmful'},
    BENEFICIAL: {bg: '#D1FAE5', fg: '#065F46', label: 'beneficial'},
    NEUTRAL: {bg: '#FEF3C7', fg: '#92400E', label: 'neutral'},
    INCONCLUSIVE: {bg: '#F3F4F6', fg: '#6B7280', label: 'inconclusive'},
  } as const;
  const tone = v && v in palette ? palette[v as keyof typeof palette] : palette.INCONCLUSIVE;
  return {...tone};
}

/**
 * **Single source of truth** for coloring anything driven by an
 * `AgentScore.score` in the scoring-chain views. Internally delegates to
 * `getScoreColor(score, direction)` so the score-direction flip (lower-
 * is-better detection agents) happens in exactly one place. Returns a
 * matching pastel bg, a saturated fg (for text + borders), and a
 * strong accent (for dots + solid fills).
 *
 * Pass the WHOLE `AgentScore` (not just the number) so this helper can
 * read `scoreDirection` off the row — that's what stops us from forgetting
 * to thread the direction through every call site.
 */
export function agentScoreTone(
  score: {score: number; scoreDirection: ScoreDirection} | null | undefined,
): {bg: string; fg: string; strong: string} {
  if (!score) {
    return {bg: pcolors.borderLight, fg: pcolors.textSecondary, strong: pcolors.borderLight};
  }
  const direction = score.scoreDirection ?? ScoreDirection.HigherIsBetter;
  const color = getScoreColor(score.score, direction);
  if (color === COLORS.GREEN) {
    return {bg: pcolors.green[50], fg: pcolors.green[700], strong: pcolors.green[500]};
  }
  if (color === COLORS.ORANGE) {
    return {bg: pcolors.amber[50], fg: pcolors.amber[700], strong: pcolors.amber[500]};
  }
  return {bg: pcolors.red[50], fg: pcolors.red[700], strong: pcolors.red[500]};
}
